import React, { useMemo, useRef, useState } from 'react';
import { __ } from '@wordpress/i18n';
import { useForm } from 'react-hook-form';

// store
import { storeSet, useStoreValue } from '../../store';

// components
import set404FromErrorResponse from '../../helpers/set404FromErrorResponse';
import FormField from '../../components/FormField';
import { Button } from 'primereact/button';
import { Toast } from 'primereact/toast';
import { Dialog } from 'primereact/dialog';

// api
import UserService from '../../service/user-service';
import AuthenticationService from '../../service/authentication-service';

// tools
import getDateFromISOstring from '../../helpers/getDateFromISOstring';

const Profile = () => {
    const isAsyncRequest = useStoreValue('isAsyncRequest');
    const userData = useStoreValue('userData');
    const toast = useRef(null);

    const {
        control,
        handleSubmit,
        formState: { errors }
    } = useForm({
        defaultValues: useMemo(() => {
            return userData;
        }, [userData]),
        mode: 'onChange'
    });

    const [isPwdDialogVisible, setIsPwdDialogVisible] = useState(false);
    const [pwdLoading, setPwdLoading] = useState(false);
    const {
        control: pwdControl,
        handleSubmit: handlePwdSubmit,
        formState: { errors: pwdErrors },
        reset: resetPwdForm
    } = useForm({ mode: 'onChange' });

    const openPwdDialog = () => {
        resetPwdForm({ password: '', newPassword: '', confirmPassword: '' });
        setIsPwdDialogVisible(true);
    };

    const closePwdDialog = () => {
        setIsPwdDialogVisible(false);
    };

    const onPwdSubmit = (formData) => {
        setPwdLoading(true);
        AuthenticationService.changePassword({
            email: userData.email,
            password: formData.password,
            newPassword: formData.newPassword,
            confirmPassword: formData.confirmPassword
        }, pwdCallback, pwdError);
    };

    const pwdCallback = (data) => {
        setPwdLoading(false);
        if (data.status === 'SUCCESS') {
            closePwdDialog();
            if (toast.current) {
                toast.current.show({
                    severity: 'success',
                    summary: '',
                    detail: data.message || __('Password aggiornata!', 'htr')
                });
            }
        } else if (toast.current) {
            toast.current.show({
                severity: 'error',
                summary: '',
                detail: data.message || __('Errore durante il cambio password', 'htr')
            });
        }
    };

    const pwdError = (data) => {
        setPwdLoading(false);
        if (toast.current) {
            toast.current.show({
                severity: 'error',
                summary: '',
                detail: (data && data.message) || __('Errore durante il cambio password', 'htr')
            });
        }
    };

    const onSubmit = (formData) => {
        storeSet('setAsyncRequest');

        UserService.updateUser(userData.id, formData, updateCallback, updateError);
    };

    const updateCallback = (data) => {
        if (data.status === 'SUCCESS') {
            storeSet('userData', data.data);
            if (toast.current) {
                toast.current.show({
                    severity: 'success',
                    summary: '',
                    detail: __('L\'utente è stato aggiornato!', 'htr')
                });
            }
        }
        storeSet('unsetAsyncRequest');
    }

    const updateError = (data) => {
        set404FromErrorResponse(data);
        storeSet('unsetAsyncRequest');
    }

    return (
        <div className="appPage">
            <div className="appPage__pageHeader">
                <h1>{__('Profilo utente', 'htr')}</h1>
            </div>

            <div className="appPage__spacer"></div>
            <Toast ref={toast}/>

            <form className="appForm" onSubmit={handleSubmit(onSubmit)}>

                <div className="appPageSection">
                    <h2>{__('Informazioni personali', 'htr')}</h2>
                    <div className="appForm__cols">
                        <FormField
                            type="textinput"
                            disabled={isAsyncRequest}
                            fieldName="firstName"
                            label={__('Nome', 'htr')}
                            control={control}
                            errors={errors}
                            config={{ required: __('È obbligatorio', 'htr') }}
                            placeholder="Mario"
                        />

                        <FormField
                            type="textinput"
                            disabled={isAsyncRequest}
                            fieldName="lastName"
                            label={__('Cognome', 'htr')}
                            control={control}
                            errors={errors}
                            config={{ required: __('È obbligatorio', 'htr') }}
                            placeholder="Rossi"
                        />

                        {/*<FormField
                            type="textinput"
                            disabled={isAsyncRequest}
                            fieldName="codiceFiscale"
                            label={__('Codice fiscale', 'htr')}
                            control={control}
                            errors={errors}
                            config={{ required: __('È obbligatorio', 'htr') }}
                            placeholder="XXXXXXXX"
                        />*/}
                    </div>
                    <div className="appForm__cols">
                        <FormField
                            type="textinput"
                            fieldName="email"
                            disabled={isAsyncRequest}
                            label={__('Email', 'htr')}
                            control={control}
                            errors={errors}
                            config={{ required: __('È obbligatorio', 'htr') }}
                            placeholder="user@example.com"
                        />

                        <FormField
                            type="textinput"
                            fieldName="phoneNumber"
                            disabled={isAsyncRequest}
                            label={__('Telefono', 'htr')}
                            control={control}
                            errors={errors}
                            placeholder="1234567"
                        />
                    </div>
                </div>

                <div className="appPageSection">
                    <h2>{__('Impostazioni', 'htr')}</h2>
                    <div className="appForm__cols">
                        <FormField
                            type="select"
                            disabled={isAsyncRequest}
                            fieldName="language"
                            defaultValue={'it'}
                            label={__('Lingua predefinita', 'htr')}
                            control={control}
                            errors={errors}
                            config={{ required: __('È obbligatorio', 'htr') }}
                            options={[
                                { label: __('Italiano', 'htr'), name: 'it' }
                            ]}
                        />

                        <FormField
                            type="select"
                            disabled={isAsyncRequest}
                            fieldName="timezone"
                                defaultValue={'Europe/Rome'}
                                label={__('Fuso Orario', 'htr')}
                                control={control}
                                errors={errors}
                                config={{ required: __('È obbligatorio', 'htr') }}
                                options={[
                                    { label: __('Europe/Rome', 'htr'), name: 'Europe/Rome' }
                                ]}
                            />

                            <FormField
                                type="select"
                                disabled={isAsyncRequest}
                                fieldName="dateformat"
                                defaultValue={'DD/MM/YY'}
                                label={__('Formato Data', 'htr')}
                                control={control}
                                errors={errors}
                                config={{ required: __('È obbligatorio', 'htr') }}
                                options={[
                                    { label: __('DD/MM/YY', 'htr'), name: 'DD/MM/YY' }
                                ]}
                            />
                        </div>
                    </div>

                    <div className="appPageSection">
                        <h2>{__('Sicurezza', 'htr')}</h2>
                        <div className="appForm__row">
                            <label>{__('Ultimo accesso', 'htr')}</label>
                            <span>{getDateFromISOstring(userData.lastLogin)}</span>
                        </div>
                        <div className="appForm__row">
                            <Button
                                type="button"
                                outlined
                                disabled={isAsyncRequest}
                                label={__('Cambia password', 'htr')}
                                icon="pi pi-lock" iconPos="right"
                                onClick={openPwdDialog}/>
                        </div>
                    </div>

                    <div className="appPage__spacer"></div>

                    <div className="appPageSection__hr">
                        <span>{__('Azioni rapide', 'htr')}</span>
                    </div>

                    <div className="appPage__spacer"></div>

                    <div className="appPageSection">
                        <div className="appPageSection__actions">
                            <Button
                                disabled={isAsyncRequest}
                                label={__('Salva modifiche', 'htr')}
                                icon="pi pi-check" iconPos="right"/>
                        </div>
                    </div>
            </form>

            <Dialog
                visible={isPwdDialogVisible}
                modal
                header={<span>{__('Cambia password', 'htr')}</span>}
                style={{ maxWidth: '480px', width: '100%' }}
                onHide={closePwdDialog}>
                <form className="appForm" onSubmit={handlePwdSubmit(onPwdSubmit)}>
                    <FormField
                        type="password"
                        fieldName="password"
                        label={__('Password attuale', 'htr')}
                        control={pwdControl}
                        errors={pwdErrors}
                        config={{ required: __('È obbligatorio', 'htr') }}
                    />
                    <FormField
                        type="password"
                        fieldName="newPassword"
                        label={__('Nuova password', 'htr')}
                        control={pwdControl}
                        errors={pwdErrors}
                        config={{
                            required: __('È obbligatorio', 'htr'),
                            validate: {
                                passEqual: (value, values) => values.confirmPassword === '' || values.confirmPassword === value
                            }
                        }}
                    />
                    <FormField
                        type="password"
                        fieldName="confirmPassword"
                        label={__('Conferma nuova password', 'htr')}
                        control={pwdControl}
                        errors={pwdErrors}
                        config={{
                            required: __('È obbligatorio', 'htr'),
                            validate: {
                                passEqual: (value, values) => values.newPassword === value
                            }
                        }}
                    />
                    <div className="appPageSection__actions">
                        <Button
                            type="button"
                            label={__('Annulla', 'htr')}
                            outlined
                            onClick={closePwdDialog}
                            disabled={pwdLoading}/>
                        <Button
                            label={__('Salva', 'htr')}
                            icon="pi pi-check" iconPos="right"
                            disabled={pwdLoading}/>
                    </div>
                </form>
            </Dialog>

        </div>
)

}

export default Profile;